import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiCheck, FiAlertCircle, FiFileText, FiList, FiPaperclip, FiMapPin } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { CamposCustomizadosRenderer } from '../../components/CamposCustomizadosRenderer';
import { AnexoUpload } from '../../components/AnexoUpload';
import { EnderecoComMapa, ENDERECO_VAZIO, enderecoComoTexto, type EnderecoMapaValor } from '../../components/EnderecoComMapa';
import { useAuth } from '../../contexts/AuthContext';
import { cartaServicosService, type AssuntoDetalhado } from '../../services/cartaServicos.service';
import { solicitacoesService } from '../../services/solicitacoes.service';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

export function SolicitarPage() {
  const { assuntoId } = useParams<{ assuntoId: string }>();
  const navigate = useNavigate();
  const { cidadao, cadastroCompleto } = useAuth();

  const [assunto, setAssunto] = useState<AssuntoDetalhado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroFatal, setErroFatal] = useState<string | null>(null);

  // Form
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [campos, setCampos] = useState<Record<string, unknown>>({});
  const [errosCampos, setErrosCampos] = useState<Record<string, string>>({});
  const [arquivosPorCategoria, setArquivosPorCategoria] = useState<Record<string, File[]>>({});
  const [arquivosLivres, setArquivosLivres] = useState<File[]>([]);

  // Localização (quando o assunto pede) — endereço + mapa com geocodificação (item 2.70)
  const [loc, setLoc] = useState<EnderecoMapaValor>(ENDERECO_VAZIO);

  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);

  useEffect(() => {
    if (!assuntoId) return;
    cartaServicosService.buscarAssunto(assuntoId)
      .then((a) => {
        if (!a) {
          setErroFatal('Serviço não encontrado ou não está disponível.');
          return;
        }
        setAssunto(a);
        setDescricao(a.modelo_inicial ?? '');
        setTitulo(a.nome);
      })
      .catch((e) => setErroFatal(e instanceof Error ? e.message : 'Erro ao carregar.'))
      .finally(() => setCarregando(false));
  }, [assuntoId]);

  function validarCampos(): boolean {
    if (!assunto) return false;
    const erros: Record<string, string> = {};
    for (const c of assunto.campos_customizados) {
      if (c.obrigatorio) {
        const v = campos[c.nome];
        const vazio = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        if (vazio) erros[c.nome] = 'Campo obrigatório.';
      }
    }
    setErrosCampos(erros);
    return Object.keys(erros).length === 0;
  }

  function validarAnexosObrigatorios(): string | null {
    if (!assunto) return null;
    for (const a of assunto.anexos_obrigatorios) {
      if (a.obrigatorio && (arquivosPorCategoria[a.id]?.length ?? 0) === 0) {
        return `Anexo obrigatório faltando: ${a.nome}.`;
      }
    }
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cidadao || !assunto) return;

    if (!validarCampos()) {
      setErroEnvio('Preencha os campos obrigatórios destacados acima.');
      return;
    }
    const erroAnexo = validarAnexosObrigatorios();
    if (erroAnexo) {
      setErroEnvio(erroAnexo);
      return;
    }
    if (titulo.trim().length < 3) {
      setErroEnvio('Informe um título mais descritivo.');
      return;
    }
    if (descricao.trim().length < 10) {
      setErroEnvio('Descreva sua solicitação com mais detalhes (mínimo 10 caracteres).');
      return;
    }

    setEnviando(true);
    setErroEnvio(null);

    try {
      // 1. Criar documento
      const doc = await solicitacoesService.criar({
        estabelecimento_id: getEstabelecimentoAtualId(),
        assunto_id: assunto.id,
        modulo_codigo: assunto.modulo_codigo,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        campos_customizados: campos,
        // Setor responsavel sera definido pelo proprio Assunto via trigger ou aqui
        setor_responsavel_id: null,
        latitude: assunto.localizacao_modo !== 'nao' ? loc.latitude : null,
        longitude: assunto.localizacao_modo !== 'nao' ? loc.longitude : null,
        endereco: assunto.localizacao_modo !== 'nao' ? (enderecoComoTexto(loc) || null) : null,
        bairro: assunto.localizacao_modo !== 'nao' ? (loc.bairro.trim() || null) : null,
        cep: assunto.localizacao_modo !== 'nao' ? (loc.cep.replace(/\D/g, '') || null) : null,
      }, cidadao.id);

      // 2. Upload de anexos por categoria
      for (const def of assunto.anexos_obrigatorios.concat(assunto.anexos_opcionais)) {
        const lista = arquivosPorCategoria[def.id] ?? [];
        for (const arquivo of lista) {
          await solicitacoesService.uploadAnexo({
            documentoId: doc.id,
            estabelecimentoId: getEstabelecimentoAtualId(),
            cidadaoId: cidadao.id,
            cidadaoNome: cidadao.nome,
            arquivo,
            categoria: def.nome,
          });
        }
      }

      // 3. Upload de anexos avulsos
      for (const arquivo of arquivosLivres) {
        await solicitacoesService.uploadAnexo({
          documentoId: doc.id,
          estabelecimentoId: getEstabelecimentoAtualId(),
          cidadaoId: cidadao.id,
          cidadaoNome: cidadao.nome,
          arquivo,
        });
      }

      navigate(`/solicitacao/${doc.numero}?criado=1`);
    } catch (err) {
      setErroEnvio(err instanceof Error ? err.message : 'Falha ao enviar solicitação.');
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <PortalPage titulo="Nova solicitação" icone={<FiFileText size={26} />} voltarHref="/servicos">
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erroFatal || !assunto) {
    return (
      <PainelResultado
        titulo="Serviço indisponível"
        descricao={erroFatal ?? 'Tente novamente.'}
        icone={<FiAlertCircle size={32} />}
        tom="aviso"
        acoes={
          <Button variant="outline" onClick={() => navigate('/servicos')}>
            <FiArrowLeft size={14} /> Voltar à carta de serviços
          </Button>
        }
      />
    );
  }

  if (!cadastroCompleto) {
    return (
      <PortalPage
        titulo={assunto.nome}
        icone={<FiFileText size={26} />}
        voltarHref="/servicos"
        voltarLabel="Voltar à carta de serviços"
      >
        <SectionCard titulo="Complete seu cadastro" icone={<FiAlertCircle size={18} />}>
          <Alert variante="aviso">
            Você precisa completar seu cadastro antes de abrir uma solicitação.
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={() => navigate('/completar-cadastro')}>
              Completar cadastro agora
            </Button>
          </div>
        </SectionCard>
      </PortalPage>
    );
  }

  return (
    <PortalPage
      titulo={assunto.nome}
      subtitulo={assunto.descricao || undefined}
      icone={<FiFileText size={26} />}
      voltarHref="/servicos"
      voltarLabel="Voltar à carta de serviços"
      heroExtra={
        assunto.prazo_dias ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/20">
            Prazo legal de {assunto.prazo_dias} dia(s) útil(eis) após o protocolo
          </span>
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erroEnvio && <Alert variante="erro">{erroEnvio}</Alert>}

        <SectionCard titulo="Sua solicitação" icone={<FiFileText size={18} />}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="titulo" obrigatorio>Título</Label>
              <Input
                id="titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                required
                placeholder="Resuma sua solicitação"
              />
            </div>
            <div>
              <Label htmlFor="descricao" obrigatorio>Descrição detalhada</Label>
              <textarea
                id="descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                required
                rows={6}
                placeholder="Descreva sua solicitação com o máximo de detalhes possível..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
            </div>
          </div>
        </SectionCard>

        {assunto.campos_customizados.length > 0 && (
          <SectionCard titulo="Informações adicionais" icone={<FiList size={18} />}>
            <CamposCustomizadosRenderer
              campos={assunto.campos_customizados}
              valores={campos}
              onChange={setCampos}
              erros={errosCampos}
            />
          </SectionCard>
        )}

        {assunto.localizacao_modo !== 'nao' && (
          <SectionCard
            titulo="Localização"
            subtitulo="Informe o CEP/endereço (preenche e marca no mapa) ou escolha direto no mapa."
            icone={<FiMapPin size={18} />}
          >
            <EnderecoComMapa
              valor={loc}
              onChange={setLoc}
              mostrarGps={assunto.localizacao_modo === 'gps' || assunto.localizacao_modo === 'ambos'}
              mostrarMapa={assunto.localizacao_modo === 'mapa' || assunto.localizacao_modo === 'ambos'}
            />
          </SectionCard>
        )}

        {(assunto.anexos_obrigatorios.length > 0 || assunto.anexos_opcionais.length > 0) && assunto.permite_anexos && (
          <SectionCard
            titulo="Anexos"
            subtitulo={`${assunto.anexos_obrigatorios.length} obrigatório(s) · ${assunto.anexos_opcionais.length} opcional(is)`}
            icone={<FiPaperclip size={18} />}
          >
            <div className="space-y-5">
              {assunto.anexos_obrigatorios.map((def) => (
                <BlocoAnexo
                  key={def.id}
                  def={def}
                  arquivos={arquivosPorCategoria[def.id] ?? []}
                  onChange={(arr) => setArquivosPorCategoria({ ...arquivosPorCategoria, [def.id]: arr })}
                />
              ))}
              {assunto.anexos_opcionais.map((def) => (
                <BlocoAnexo
                  key={def.id}
                  def={def}
                  arquivos={arquivosPorCategoria[def.id] ?? []}
                  onChange={(arr) => setArquivosPorCategoria({ ...arquivosPorCategoria, [def.id]: arr })}
                />
              ))}

              <div>
                <Label>Outros anexos (opcional)</Label>
                <AnexoUpload
                  arquivos={arquivosLivres}
                  onChange={setArquivosLivres}
                  tamanhoMaxMB={20}
                  multiplo
                  maxArquivos={5}
                />
              </div>
            </div>
          </SectionCard>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/servicos')}>
            Cancelar
          </Button>
          <Button type="submit" loading={enviando}>
            <FiSend size={14} /> Enviar solicitação
          </Button>
        </div>

        <p className="text-xs text-slate-500 text-center">
          Ao enviar você confirma que as informações são verdadeiras. A solicitação será protocolada
          e você poderá acompanhá-la em "Minhas solicitações".
        </p>
      </form>
    </PortalPage>
  );
}

function BlocoAnexo({
  def, arquivos, onChange,
}: {
  def: AssuntoDetalhado['anexos_obrigatorios'][number];
  arquivos: File[];
  onChange: (a: File[]) => void;
}) {
  return (
    <div>
      <Label obrigatorio={def.obrigatorio}>
        {def.nome}
      </Label>
      {def.descricao && <p className="text-xs text-slate-500 mb-2">{def.descricao}</p>}
      <AnexoUpload
        arquivos={arquivos}
        onChange={onChange}
        extensoesPermitidas={def.extensoes_permitidas}
        tamanhoMaxMB={def.tamanho_max_mb}
        multiplo={false}
      />
      {arquivos.length > 0 && def.obrigatorio && (
        <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
          <FiCheck size={12} /> Anexo recebido
        </p>
      )}
    </div>
  );
}
